/**
 * Inteligência de Custo: quanto custa cuidar da carteira e onde dá para pagar
 * menos. Área própria (saiu de Contabilidade › Resultado por Plano › Diagnóstico)
 * porque a pergunta é de gestão do custo assistencial, não de contabilidade.
 *
 * Subtelas entram aqui conforme ficam prontas; hoje: Rede e preços e Internações.
 * Permissão: reusa o módulo resultado_plano (mesmos dados de utilização; módulo
 * novo no catálogo não chegaria aos perfis de instalações existentes).
 */
import React, { useEffect, useState } from "react";
import { Coins, Network, BedDouble } from "lucide-react";
import toast from "react-hot-toast";
import api from "../services/api";
import InfoTip from "../components/InfoTip";
import { useAuth } from "../context/AuthContext";
import RedePrecos from "./custo/RedePrecos";
import Internacoes from "./custo/Internacoes";

const T = { ink: "#2c3e50", inkSoft: "#7f8c8d", line: "#e8e8e8", teal: "var(--brand, #20a490)", tealSoft: "#e8f6f4" };
const campo = { fontSize: 13, padding: "6px 10px", border: `1px solid ${T.line}`, borderRadius: 6, background: "#fff" };
const mesBR = (m) => (m ? `${m.slice(5, 7)}/${m.slice(0, 4)}` : "");
const mesCorrente = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; };

const SUBTELAS = [
  { id: "rede", rotulo: "Rede e preços", icone: Network },
  { id: "internacoes", rotulo: "Internações", icone: BedDouble },
];

const TIP_AREA = "Reúne as análises do custo assistencial: quanto a operadora paga pelo cuidado da carteira, por quê, e onde dá para pagar menos. A base são as contas médicas por competência (data de apresentação), com o valor pago ao prestador. O mês corrente fica de fora da escolha inicial porque as contas dele ainda estão chegando.";

export default function InteligenciaCusto() {
  const { temNivel } = useAuth();
  const gestor = temNivel("inteligencia_custo", "gestor") || temNivel("resultado_plano", "gestor");
  const [meses, setMeses] = useState(null);
  const [mes, setMes] = useState("");
  const [tp, setTp] = useState("");
  const [sub, setSub] = useState("rede");

  useEffect(() => {
    api.get("/resultado-plano/utilizacao/meses")
      .then((r) => {
        const ms = r.data || [];
        setMeses(ms);
        const fechados = ms.filter((m) => m < mesCorrente());
        setMes(fechados.length ? fechados[fechados.length - 1] : ms[ms.length - 1] || "");
      })
      .catch(() => { setMeses([]); toast.error("Falha ao carregar os meses da utilização"); });
  }, []);

  return (
    <div style={{ padding: "20px 24px", maxWidth: 1400 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <Coins size={22} color="var(--brand, #20a490)" />
        <h1 style={{ fontSize: 22, margin: 0, color: T.ink }}>Inteligência de Custo</h1>
        <InfoTip w={420} texto={TIP_AREA} />
      </div>
      <p style={{ fontSize: 13, color: T.inkSoft, margin: "4px 0 14px" }}>
        Quanto custa cuidar da carteira e onde dá para pagar menos.
      </p>

      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "flex-end", borderBottom: `1px solid ${T.line}`, paddingBottom: 12 }}>
        <div role="tablist" aria-label="Subtelas" style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {SUBTELAS.map((s) => {
            const Icone = s.icone;
            const ativa = sub === s.id;
            return (
              <button key={s.id} role="tab" aria-selected={ativa} onClick={() => setSub(s.id)}
                      style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 14px", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer",
                               border: `1px solid ${ativa ? T.teal : T.line}`, background: ativa ? T.tealSoft : "#fff", color: ativa ? T.teal : T.ink }}>
                <Icone size={15} /> {s.rotulo}
              </button>
            );
          })}
        </div>
        <div style={{ flex: 1 }} />
        {sub === "rede" && <label style={{ display: "flex", flexDirection: "column", gap: 3, fontSize: 11, color: T.inkSoft }}>
          Competência
          <select value={mes} onChange={(e) => setMes(e.target.value)} style={campo} disabled={!meses?.length}>
            {(meses || []).slice().reverse().map((m) => <option key={m} value={m}>{mesBR(m)}{m >= mesCorrente() ? " (em andamento)" : ""}</option>)}
          </select>
        </label>}
        <label style={{ display: "flex", flexDirection: "column", gap: 3, fontSize: 11, color: T.inkSoft }}>
          Regime do plano
          <select value={tp} onChange={(e) => setTp(e.target.value)} style={campo}>
            <option value="">Todos</option>
            <option value="P">Pré-pagamento</option>
            <option value="O">Pós-pagamento</option>
          </select>
        </label>
      </div>

      {meses === null && <div style={{ marginTop: 16, color: T.inkSoft, fontSize: 13 }}>Carregando…</div>}
      {meses && !meses.length && <div style={{ marginTop: 16, color: T.inkSoft, fontSize: 13 }}>A utilização ainda não foi sincronizada nesta instalação.</div>}
      {mes && sub === "rede" && <RedePrecos mes={mes} tp={tp} gestor={gestor} meses={meses || []} />}
      {sub === "internacoes" && <Internacoes tp={tp} />}
    </div>
  );
}
